import { useTranslation } from "react-i18next";
import {
  Avatar,
  Button,
  Dropdown,
  Form,
  Layout,
  MenuProps,
  message,
  Modal,
  TableColumnsType,
  TableProps,
  Tooltip,
  Typography,
} from "antd";
import PageHeader from "../../../../../components/page-header/PageHeader";
import FilterTable from "./component/filter-table/FilterTable";
import TableData from "../../../../../components/table-data/TableData";
import { useEffect, useState } from "react";
import { DataType } from "./constants/data";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  HistoryOutlined,
  MoreOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { isEmpty } from "lodash";
import {
  deleteManyQuizInQuestionBank,
  deleteQuizInQuestionBank,
  duplicateQuizToQuestionBank,
  moveQuizToQuestionBank,
  saveVersionMultipleQuizzes,
  searchQuizInQuestionBank,
} from "../../../../../service/library";
import { getQuizType } from "../../../../../utils/getQuizType";
import { formatDateToIsoStringHHmm } from "../../../../../utils/converDate";
import "./question-bank-detail.scss";
import QuizPreview from "../../../../../components/quiz-preview/QuizPreview";
import ListQuestionBankModal from "../list-question-bank-modal/ListQuestionBankModal";
import { useAddLesson } from "../../../../../stores/stores";
import { ModeQuestion } from "../../../../../constants";
import { convertLatexToImage, stripHtml } from "../../../../../utils/app";

const QuestionBankDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { question, setQuestion, setModeQuestion } = useAddLesson();
  const location = useLocation();
  const navigate = useNavigate();

  const params = new URLSearchParams(location.search);
  const name = params.get("name");
  const permission = params.get("permission");

  const [dataQuiz, setDataQuiz] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [moveLoading, setMoveLoading] = useState<boolean>(false);
  const [statusOrder, setStatusOrder] = useState<string>("");
  const [fieldSortOrder, setFieldSortOrder] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalRecords, setTotalRecords] = useState<number>(0);

  const [quizDetailId, setQuizDetailId] = useState<number>(0);
  const [showUsedTable, setShowUsedTable] = useState<boolean>(false);

  const [isOpenQuizPreviewModal, setIsOpenQuizPreviewModal] =
    useState<boolean>(false);
  const [isOpenListQuestionBankModal, setIsOpenListQuestionBankModal] =
    useState<boolean>(false);

  const [selectedIds, setSelectedIds] = useState<any>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any>([]);

  // handle delete unit bank
  const handleDeleteQuiz = async (quizId: number) => {
    try {
      const res = await deleteQuizInQuestionBank(quizId);
      if (res.status === 200) {
        message.success(t("Xóa thành công", "Xóa thành công"));
        fetchData();
      }
    } catch (error) {
      console.error(error);
    }
  };

  // handle duplicate unit bank
  const duplicateQuiz = async (quizId: number) => {
    try {
      const res = await duplicateQuizToQuestionBank(quizId);
      if (res.status === 200) {
        message.success(t("Tạo bản sao thành công", "Tạo bản sao thành công"));
        fetchData();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleGoToEdit = (quizId: any) => {
    if (!quizId) return;
    setModeQuestion(ModeQuestion.UPDATE);
    navigate(
      `${location.pathname}/add-questions?name=${name}&quizId=${quizId}`
    );
  };

  // quiz dropdown action
  const getMenuItems = (item: any): MenuProps["items"] => [
    {
      key: "1",
      label: (
        <div
          onClick={() => {
            setShowUsedTable(false);
            setQuizDetailId(item.id);
            setIsOpenQuizPreviewModal(true);
          }}
        >
          <EyeOutlined />
          {t("&nbsp; Xem trước", "&nbsp; Xem trước")}
        </div>
      ),
    },
    ...(permission === "edit"
      ? [
          {
            key: "2",
            label: (
              <div
                onClick={() => {
                  handleGoToEdit(item.id);
                  setQuestion({ ...question, id: item.id });
                }}
              >
                <EditOutlined /> {t("Chỉnh sửa", "Chỉnh sửa")}{" "}
              </div>
            ),
          },
        ]
      : []),
    ...(item.questionBankVersion && permission === "edit"
      ? [
          {
            key: "3",
            label: (
              <div onClick={() => duplicateQuiz(item.id)}>
                <CopyOutlined />
                {t("&nbsp; Tạo bản sao", "&nbsp; Tạo bản sao")}
              </div>
            ),
          },
        ]
      : []),
    ...(permission === "edit"
      ? [
          {
            key: "4",
            label: (
              <div
                style={{ color: "#ff4d4f" }}
                onClick={() =>
                  Modal.confirm({
                    title: `Xóa câu hỏi`,
                    content: `Dữ liệu đã xoá sẽ không thể khôi phục. Bạn có chắc chắn muốn xoá?`,
                    onOk: () => handleDeleteQuiz(item.id),
                    centered: true,
                    okText: t("Xác nhận", "Xác nhận"),
                    cancelText: t("Huỷ bỏ", "Huỷ bỏ"),
                    footer: (_, { OkBtn, CancelBtn }) => (
                      <>
                        <CancelBtn />
                        <OkBtn />
                      </>
                    ),
                  })
                }
              >
                <DeleteOutlined />
                {t("&nbsp; Xóa", "&nbsp; Xóa")}
              </div>
            ),
          },
        ]
      : []),
    ...(item.questionBankVersion
      ? [
          {
            key: "5",
            label: (
              <div
                onClick={() =>
                  navigate(
                    `${location.pathname}/versions?name=${name}&quizId=${item.id}&permission=${permission}`
                  )
                }
              >
                <HistoryOutlined />
                {t("&nbsp; Danh sách phiên bản", "&nbsp; Danh sách phiên bản")}
              </div>
            ),
          },
        ]
      : []),
  ];

  const dataColumns: TableColumnsType<DataType> = [
    {
      title: "STT",
      dataIndex: "numericalOrder",
      key: "numericalOrder",
      width: 5,
      render: (_: any, record, index: number) => {
        return <div>{record?.numericalOrder}</div>;
      },
    },
    {
      title: t("Câu hỏi", "Câu hỏi"),
      dataIndex: "question",
      key: "question",
      width: 50,
      className: "question-col",
      render: (_: any, record, index: number) => {
        return (
          <div
            className="question-content"
            dangerouslySetInnerHTML={{
              __html: `${convertLatexToImage(record?.question)}`,
            }}
          ></div>
        );
      },
    },
    {
      title: t("Loại câu hỏi", "Loại câu hỏi"),
      dataIndex: "type",
      key: "type",
      render: (_: any, record) => {
        return <span>{getQuizType(Number(record.type))}</span>;
      },
      width: 20,
    },
    {
      title: t("Phiên bản", "Phiên bản"),
      dataIndex: "questionBankVersion",
      key: "questionBankVersion",
      render: (_: any, record) => {
        return record.questionBankVersion ? (
          <span>{record.questionBankVersion}</span>
        ) : (
          <div className="flex">
            {t("Bản nháp", "Bản nháp")}
            <Tooltip
              title={
                <div
                  style={{
                    maxWidth: 200,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  Khi ở chế độ lưu nháp câu hỏi chưa thể sử dụng tại các bài
                  đánh giá trong khoá học
                </div>
              }
              mouseEnterDelay={0.1}
            >
              <QuestionCircleOutlined
                className="ml-1"
                style={{ color: "#1677FF" }}
              />
            </Tooltip>
          </div>
        );
      },
      width: 15,
    },
    {
      title: t("Lượt sử dụng", "Lượt sử dụng"),
      dataIndex: "numberOfUses",
      key: "numberOfUses",
      render: (_: any, record: any) => {
        return (
          <Typography.Link
            onClick={() =>
              navigate(
                `${location.pathname}/used?name=${name}&quizId=${record.id}&version=${record.questionBankVersion}`
              )
            }
          >
            {record.numberOfUses}
          </Typography.Link>
        );
      },
      width: 15,
    },
    {
      title: t("Chủ sở hữu", "Chủ sở hữu"),
      dataIndex: "author",
      key: "author",
      render: (_: any, record: any) => {
        return (
          <div className="quiz-author">
            <div>
              <Avatar src={record.author?.avatar} />
            </div>
            <div className="quiz-author__info">
              <span className="quiz-author__info-email">
                {record.author?.email}
              </span>
              <span>
                {record.createdDate
                  ? formatDateToIsoStringHHmm(record.createdDate)
                  : null}
              </span>
            </div>
          </div>
        );
      },
      width: 30,
      className: "author-col",
    },
    {
      title: t("Chỉnh sửa lần cuối", "Chỉnh sửa lần cuối"),
      dataIndex: "editor",
      key: "editor",
      render: (_: any, record: any) => {
        return (
          <div className="quiz-editor">
            <div>
              <Avatar
                src={
                  record.editor ? record.editor?.avatar : record.author?.avatar
                }
              />
            </div>
            <div className="quiz-editor__info">
              <span className="quiz-editor__info-email">
                {record.editor ? record.editor?.email : record.author?.email}
              </span>
              <span>
                {record.updatedDate
                  ? formatDateToIsoStringHHmm(record.updatedDate)
                  : formatDateToIsoStringHHmm(record.createdDate)}
              </span>
            </div>
          </div>
        );
      },
      width: 30,
      className: "editor-col",
    },
    {
      title: "",
      dataIndex: "action",
      key: "operation",
      render: (_, record) => {
        return (
          <Dropdown
            menu={{ items: getMenuItems(record) }}
            placement="bottomRight"
          >
            <span
              style={{ display: "inline-block", padding: 4, cursor: "pointer" }}
            >
              <MoreOutlined style={{ fontSize: 18 }} />
            </span>
          </Dropdown>
        );
      },
      fixed: "right",
      width: 10,
    },
  ];

  // Sort
  const handleTableChange: TableProps["onChange"] = (
    pagination,
    filters,
    sorter: any
  ) => {
    const data =
      sorter.order === "ascend"
        ? "asc"
        : sorter.order === "descend"
        ? "desc"
        : "";
    setStatusOrder(data);
    setFieldSortOrder(sorter.field);
  };

  // fetch data
  const fetchData = async () => {
    const dataReq = {
      bankId: id,
      keyWord: "",
      size: pageSize,
      page: pageNumber,
      sort:
        isEmpty(fieldSortOrder) || isEmpty(statusOrder)
          ? ["created_date, desc"]
          : [`${fieldSortOrder},${statusOrder}`],
    };

    try {
      setLoading(true);
      const res = await searchQuizInQuestionBank(dataReq);
      if (res.status === 200) {
        setSelectedIds([]);
        setSelectedRowKeys([]);
        setDataQuiz(res.data?.data?.content);
        setTotalRecords(res.data?.data?.totalElements);
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // handle multi save version quiz
  const handleSaveVersionMultipleQuiz = async () => {
    try {
      const res = await saveVersionMultipleQuizzes({ids: selectedIds});
      if (res.status === 200) {
        message.success(t("Lưu phiên bản thành công", "Lưu phiên bản thành công"));
        fetchData();
      }
    } catch (error) {
      console.error(error);
    }
  };

  // handle multi delete quiz
  const handleDeleteManyQuiz = async () => {
    try {
      const res = await deleteManyQuizInQuestionBank(selectedIds);
      if (res.status === 200) {
        message.success(t("Xóa thành công", "Xóa thành công"));
        fetchData();
      }
    } catch (error) {
      console.error(error);
    }
  };

  // handle move quiz
  const hanldeMoveQuizToQuestionBank = async (bankId: number) => {
    setMoveLoading(true);
    const dataReq = {
      questionBankId: bankId,
      quizIds: selectedIds,
    };

    try {
      const res = await moveQuizToQuestionBank(dataReq);
      if (res.status === 200) {
        message.success(t("Di chuyển thành công", "Di chuyển thành công"));
        setMoveLoading(false);
        setIsOpenListQuestionBankModal(false);
        fetchData();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setMoveLoading(false);
      setIsOpenListQuestionBankModal(false);
    }
  };

  // useEffect
  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id, pageNumber, pageSize]);

  useEffect(() => {
    if (selectedRowKeys.length > 0) {
      const ids = selectedRowKeys.map((item: any) => item.id);
      setSelectedIds(ids);
    }
  }, [selectedRowKeys]);

  return (
    <Layout className="page-header-group unit-list-page">
      <Form.Provider>
        <div className={`page-header-group--layout`}>
          <div className="table-header--wrapper">
            <div className="table-header--left">
              <PageHeader title={`Ngân hàng câu hỏi/ ${name}`} />
            </div>
            <FilterTable
              selectedRowKeys={selectedRowKeys}
              handleDeleteManyQuiz={handleDeleteManyQuiz}
              handleSaveVersionMultipleQuiz={handleSaveVersionMultipleQuiz}
              setIsOpenListQuestionBankModal={setIsOpenListQuestionBankModal}
            />
          </div>
        </div>
        <TableData
          dataTable={dataQuiz}
          dataColumns={dataColumns}
          setPageNumber={setPageNumber}
          setPageSize={setPageSize}
          pageNumber={pageNumber}
          pageSize={pageSize}
          pagination={true}
          scroll={{ x: 1500, y: 600 }}
          totalRecords={totalRecords}
          loadingTable={loading}
          handleTableChangeProps={handleTableChange}
          hideSelection={permission !== "view"}
          selectionColumnWidth={5}
          rowSelected={selectedRowKeys}
          setSelectedRowKeysProps={setSelectedRowKeys}
        />
      </Form.Provider>
      <ListQuestionBankModal
        mode="move"
        moveLoading={moveLoading}
        isOpenModal={isOpenListQuestionBankModal}
        setIsOpenModal={setIsOpenListQuestionBankModal}
        hanldeMoveQuizToQuestionBank={hanldeMoveQuizToQuestionBank}
      />
      <QuizPreview
        fullHeight
        isOpenModal={isOpenQuizPreviewModal}
        setIsOpenModal={setIsOpenQuizPreviewModal}
        quizId={quizDetailId}
        setQuizDetailId={setQuizDetailId}
        showUsedTable={showUsedTable}
      />
    </Layout>
  );
};

export default QuestionBankDetail;
